import { CNS_API_BASE } from "@/config";
import type {
  ConnectorOAuthAttempt,
  ConnectorOAuthAttemptStatus,
  ConnectorOAuthAuthorization,
  ConnectorOAuthContinuationExecution,
  ConnectorOAuthResolvedContext,
  ConnectorOAuthStartContext,
  ConnectorOAuthSurface,
} from "@/models/connectorOAuth";
import type { CNSPluginConnectionIntent } from "@/models/plugin";
import { throwOnProblemResponse } from "@/types/problem-detail";

const ATTEMPT_STATUSES: ConnectorOAuthAttemptStatus[] = [
  "preparing",
  "pending",
  "exchanging",
  "succeeded",
  "failed",
  "cancelled",
  "expired",
];

const SURFACES: ConnectorOAuthSurface[] = [
  "settings",
  "source_picker",
  "chat_connect_source",
  "plugins",
];

const CONNECTION_INTENTS: CNSPluginConnectionIntent[] = [
  "connect",
  "connect_and_enable",
];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

function asNullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return asString(value);
}

function asStatus(value: unknown): ConnectorOAuthAttemptStatus | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.toLowerCase() as ConnectorOAuthAttemptStatus;
  return ATTEMPT_STATUSES.includes(normalized) ? normalized : undefined;
}

function asSurface(value: unknown): ConnectorOAuthSurface | null | undefined {
  if (value === null) return null;
  if (typeof value !== "string") return undefined;
  const normalized = value.toLowerCase() as ConnectorOAuthSurface;
  return SURFACES.includes(normalized) ? normalized : undefined;
}

function asConnectionIntent(
  value: unknown,
): CNSPluginConnectionIntent | null | undefined {
  if (value === null) return null;
  if (typeof value !== "string") return undefined;
  const normalized = value as CNSPluginConnectionIntent;
  return CONNECTION_INTENTS.includes(normalized) ? normalized : undefined;
}

const CONTINUATION_EXECUTIONS: ConnectorOAuthContinuationExecution[] = [
  "none",
  "running",
  "completed",
];

function asContinuationExecution(
  value: unknown,
): ConnectorOAuthContinuationExecution | null | undefined {
  if (value === null) return null;
  if (typeof value !== "string") return undefined;
  const normalized = value.toLowerCase() as ConnectorOAuthContinuationExecution;
  return CONTINUATION_EXECUTIONS.includes(normalized) ? normalized : undefined;
}

function normalizeResolvedContext(value: unknown): ConnectorOAuthResolvedContext {
  const record = asRecord(value);
  if (!record) {
    throw new Error("Connector OAuth context response is missing required fields");
  }
  const workspacePublicId = asString(record.workspacePublicId);
  const surface = asSurface(record.surface);
  const returnPath = asString(record.returnPath);
  if (!workspacePublicId || !surface || !returnPath) {
    throw new Error("Connector OAuth context response is missing required fields");
  }
  return {
    workspacePublicId,
    surface,
    returnPath,
    connectionPublicId: asString(record.connectionPublicId),
    projectPublicId: asString(record.projectPublicId),
    conversationPublicId: asString(record.conversationPublicId),
    toolCallId: asString(record.toolCallId),
    pluginKey: asString(record.pluginKey),
    connectionOptionKey: asString(record.connectionOptionKey),
    connectionIntent: asConnectionIntent(record.connectionIntent) ?? undefined,
  };
}

function normalizeAuthorization(value: unknown): ConnectorOAuthAuthorization {
  const record = asRecord(value);
  if (!record) {
    throw new Error("Invalid connector OAuth authorization response");
  }
  const attemptPublicId =
    asString(record.attemptPublicId) ?? asString(record.id);
  const authorizationUrl = asString(record.authorizationUrl);
  const credentialPublicId =
    asString(record.credentialPublicId) ??
    asString(record.sourceCredentialPublicId) ??
    asString(record.credentialId);
  const expiresAt = asString(record.expiresAt);
  if (!attemptPublicId || !authorizationUrl || !credentialPublicId || !expiresAt) {
    throw new Error("Connector OAuth authorization response is missing required fields");
  }
  return {
    attemptPublicId,
    authorizationUrl,
    credentialPublicId,
    expiresAt,
    context: normalizeResolvedContext(record.context),
  };
}

function normalizeAttempt(value: unknown): ConnectorOAuthAttempt {
  const record = asRecord(value);
  if (!record) {
    throw new Error("Invalid connector OAuth attempt response");
  }

  const attemptPublicId =
    asString(record.attemptPublicId) ?? asString(record.id);
  const credentialPublicId =
    asString(record.credentialPublicId) ??
    asString(record.sourceCredentialPublicId) ??
    asString(record.credentialId);
  const status = asStatus(record.status);
  const errorRecord = asRecord(record.error);
  const toolResultAppliedAt = asNullableString(record.toolResultAppliedAt);
  const contextRecord = asRecord(record.context);

  if (!attemptPublicId || !credentialPublicId || !status) {
    throw new Error("Connector OAuth attempt response is missing required fields");
  }

  return {
    attemptPublicId,
    credentialPublicId,
    provider: asNullableString(record.provider),
    workspacePublicId:
      asNullableString(record.workspacePublicId) ??
      asNullableString(contextRecord?.workspacePublicId),
    status,
    scope: asNullableString(record.scope) ?? asNullableString(record.scopes),
    error:
      asNullableString(record.error) ??
      asNullableString(errorRecord?.detail) ??
      asNullableString(errorRecord?.title) ??
      asNullableString(record.errorDetail) ??
      asNullableString(record.errorMessage),
    errorCode:
      asNullableString(record.errorCode) ??
      asNullableString(errorRecord?.code),
    toolResultApplied:
      record.toolResultApplied === true || typeof toolResultAppliedAt === "string",
    toolResultAppliedAt,
    continuationClaimedAt: asNullableString(record.continuationClaimedAt),
    claimedAssistantMessageId:
      asNullableString(record.claimedAssistantMessageId) ??
      asNullableString(record.claimedAssistantMessagePublicId),
    continuationExecution: asContinuationExecution(record.continuationExecution),
    surface:
      asSurface(record.surface) ?? asSurface(contextRecord?.surface) ?? null,
    returnPath:
      asNullableString(record.returnPath) ??
      asNullableString(contextRecord?.returnPath) ??
      null,
    projectPublicId:
      asNullableString(record.projectPublicId) ??
      asNullableString(contextRecord?.projectPublicId) ??
      null,
    conversationPublicId:
      asNullableString(record.conversationPublicId) ??
      asNullableString(contextRecord?.conversationPublicId) ??
      null,
    toolCallId:
      asNullableString(record.toolCallId) ??
      asNullableString(contextRecord?.toolCallId) ??
      null,
    connectionPublicId:
      asNullableString(record.connectionPublicId) ??
      asNullableString(contextRecord?.connectionPublicId) ??
      null,
    pluginKey:
      asNullableString(record.pluginKey) ??
      asNullableString(contextRecord?.pluginKey) ??
      null,
    connectionOptionKey:
      asNullableString(record.connectionOptionKey) ??
      asNullableString(contextRecord?.connectionOptionKey) ??
      null,
    connectionIntent:
      asConnectionIntent(record.connectionIntent) ??
      asConnectionIntent(contextRecord?.connectionIntent) ??
      null,
    createdAt: asNullableString(record.createdAt),
    updatedAt: asNullableString(record.updatedAt),
    expiresAt: asNullableString(record.expiresAt),
    exchangeExpiresAt: asNullableString(record.exchangeExpiresAt),
  };
}

/**
 * Starts `POST /source-credentials/{credentialId}/oauth/authorize`.
 *
 * The abort signal applies only to the authorization-start request. An attempt created by the
 * backend remains recoverable through the active-attempt endpoint.
 */
export async function authorizeSourceCredentialOAuth(
  credentialId: string,
  context?: ConnectorOAuthStartContext,
  signal?: AbortSignal,
): Promise<ConnectorOAuthAuthorization> {
  const response = await fetch(
    `${CNS_API_BASE}/source-credentials/${encodeURIComponent(credentialId)}/oauth/authorize`,
    {
      method: "POST",
      credentials: "include",
      headers: context ? { "Content-Type": "application/json" } : undefined,
      body: context ? JSON.stringify(context) : undefined,
      signal,
    },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to start plugin authorization");
  }
  return normalizeAuthorization(await response.json());
}

/** Reads the backend-authoritative OAuth attempt. */
export async function fetchConnectorOAuthAttempt(
  attemptPublicId: string,
  signal?: AbortSignal,
): Promise<ConnectorOAuthAttempt> {
  const response = await fetch(
    `${CNS_API_BASE}/source-oauth-attempts/${encodeURIComponent(attemptPublicId)}`,
    { credentials: "include", signal },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to confirm plugin authorization");
  }
  return normalizeAttempt(await response.json());
}

/** Lists active OAuth attempts for the authenticated user. */
export async function fetchActiveConnectorOAuthAttempts(
  signal?: AbortSignal,
): Promise<ConnectorOAuthAttempt[]> {
  const response = await fetch(`${CNS_API_BASE}/source-oauth-attempts`, {
    credentials: "include",
    signal,
  });
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to recover plugin authorizations");
  }

  const payload = await response.json();
  const values = Array.isArray(payload)
    ? payload
    : Array.isArray(asRecord(payload)?.content)
      ? (asRecord(payload)?.content as unknown[])
      : [];
  return values.map(normalizeAttempt);
}

/** Cancels an OAuth attempt and returns its backend-confirmed status. */
export async function cancelConnectorOAuthAttempt(
  attemptPublicId: string,
): Promise<ConnectorOAuthAttempt> {
  const response = await fetch(
    `${CNS_API_BASE}/source-oauth-attempts/${encodeURIComponent(attemptPublicId)}/cancel`,
    { method: "POST", credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to cancel plugin authorization");
  }
  if (response.status === 204) {
    return fetchConnectorOAuthAttempt(attemptPublicId);
  }
  const body = await response.text();
  if (!body.trim()) {
    return fetchConnectorOAuthAttempt(attemptPublicId);
  }
  try {
    return normalizeAttempt(JSON.parse(body));
  } catch {
    return fetchConnectorOAuthAttempt(attemptPublicId);
  }
}
